import { HpCompleteComponentV10, HpComponentNotFullV2, HpComponentWsV2 } from 'api3';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, ViewChild, computed, effect, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from '@app/services/message.service';
import { SvgComponent } from '@app/components/svg/svg.component';
import { TabsComponent } from '@app/components/tabs/tabs.component';
import { StepsComponent } from '../steps/steps.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { Rect } from '@svgdotjs/svg.js';
import { AccordionModule } from 'primeng/accordion';
import { Button } from 'primeng/button';
import { TabsModule } from 'primeng/tabs';
import { ToggleSwitch } from 'primeng/toggleswitch';

@Component({
  selector: 'app-step-by-step-diagnosis',
  imports: [CommonModule, TranslocoModule, Button, AccordionModule, SvgComponent, TabsModule, TabsComponent, StepsComponent, ToggleSwitch],
  templateUrl: './step-by-step-diagnosis.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 auto;
        height: 100%;
        width: 100%;
      }
    `,
  ],
  standalone: true,
})
export class StepByStepDiagnosisComponent {
  translocoService = inject(TranslocoService);

  carTypeId = input<number | undefined>();
  component = input<HpComponentNotFullV2 | undefined>(undefined);
  @Output() onFinish = new EventEmitter<void>();
  @Output() onBackClick = new EventEmitter<void>();
  @ViewChild('svgComponent') svgComponent: SvgComponent | undefined;
  @ViewChild('pinBoardFrontSvg') pinBoardFrontSvg: SvgComponent | undefined;
  @ViewChild('pinBoardBackSvg') pinBoardBackSvg: SvgComponent | undefined;

  messageService = inject(MessageService);
  router = inject(Router);

  completeComponentData = signal<HpCompleteComponentV10 | undefined>(undefined);
  isLoading = signal<boolean>(false);
  activeStep = signal(-1);
  highlightedComponentIds = signal<Rect[]>([]);

  stepsCount = computed<number>(() => this.completeComponentData()?.diagnosisSteps?.[0]?.diagnosisSteps?.length || 0);
  hasPlugSocketImage = computed<boolean>(() => !!this.completeComponentData()?.connectorSideImage || !!this.completeComponentData()?.ecuSideImage);
  pinTable = computed<HpComponentWsV2[]>(() =>
    (this.completeComponentData()?.pinTable?.componentsArray || []).filter((pin) => pin.name != null).sort((a, b) => (a.name! > b.name! ? 1 : -1)),
  );
  selectedTab = 0;
  tabs = signal<any[]>([]);

  onChange = effect(async () => {
    const hasPlugSocketImage = this.hasPlugSocketImage();
    const text0 = await this.translocoService.translate('diagnosis.tabs.wiring-diagram');
    const text1 = await this.translocoService.translate('diagnosis.tabs.pins-view');
    const text2 = await this.translocoService.translate('diagnosis.tabs.location');
    const text3 = await this.translocoService.translate('diagnosis.tabs.replacement');
    const text4 = await this.translocoService.translate('diagnosis.tabs.partsInfo');

    const tabs = [
      { value: 0, title: text0, content: '' },
      { value: 1, title: text1, content: '', disabled: !hasPlugSocketImage },
      { value: 2, title: text2, content: '', disabled: true },
      { value: 3, title: text3, content: '', disabled: true },
      { value: 4, title: text4, content: '', disabled: true },
    ];
    this.selectedTab = tabs[0].value;
    this.tabs.set(tabs);
  });

  onChangeTab(selectedValue: any): boolean {
    this.selectedTab = selectedValue;
    return true;
  }

  onInputChange = effect(async () => {
    this.isLoading.set(true);
    const completeComponentData = await this._loadCompleteComponentData();
    this.completeComponentData.set(completeComponentData);
    this.isLoading.set(false);
  });

  onStepChange = effect(() => {
    if (this.activeStep() === undefined) {
      this.svgComponent?.removeAllPins();
    }
  });

  getTableKey(id: string | null, index: number): string {
    return `${id ?? Math.random().toString(36).slice(2)}-${index}`;
  }

  hoverTableRow(componentId: number | null) {
    if (!componentId) return;
    this.onLeaveRow();

    const highlightRects = this.svgComponent?.highlightSvgComponent(String(componentId), true);

    if (highlightRects) {
      this.highlightedComponentIds.update((list) => {
        highlightRects.forEach((rect) => list.push(rect));
        return list;
      });
    }
  }

  onLeaveRow() {
    this.highlightedComponentIds().forEach((rect) => {
      this.svgComponent?.clearComponentHighlight(rect);
    });
    this.highlightedComponentIds.set([]);
  }

  displayPlugContact(checked: boolean, pin: HpComponentWsV2) {
    const pinPort = pin.pinNumber?.replace(/[^a-zA-Z0-9]/g, '');
    if (pinPort) {
      if (checked) {
        this.pinBoardFrontSvg?.highlightPin(pinPort);
        this.pinBoardBackSvg?.highlightPin(pinPort);
      } else {
        this.pinBoardFrontSvg?.clearPinHighlight(pinPort);
        this.pinBoardBackSvg?.clearPinHighlight(pinPort);
      }
    }
  }

  onLastQuestionIsAnswered() {
    if (this.activeStep() === this.stepsCount() - 1) {
      this.onFinish?.emit();
    } else {
      this.nextItem();
    }
  }

  nextItem() {
    this.activeStep.set((this.activeStep() + 1) % this.stepsCount());
  }

  goBack() {
    // navigate back ..
    // this.location.back();
    // this.router.navigate(['..'], { relativeTo: this.route });

    // .. or remove componentId from url
    this.router.navigate([], {
      relativeTo: this.router.routerState.root,
      queryParams: {
        componentId: undefined,
      },
      queryParamsHandling: 'merge',
    });
  }

  private async _loadCompleteComponentData() {
    const carTypeId = this.carTypeId();
    const component = this.component();
    const symbolInstanceId = component?.symbolInstanceId;
    const systemTypeId = component?.systemTypeId;
    if (!carTypeId || !symbolInstanceId || !systemTypeId) return;

    return await this.messageService.getCompleteDiagnosticComponent(carTypeId, symbolInstanceId, systemTypeId);
  }
}
